import {
  OPERATORS,
  type OperatorSlug,
  OTHER_OPERATOR_SLUG,
  resolveOperator,
} from '../domain/operators.js'
import { normalizeEvseId } from './keys.js'
import type { GeAddress } from './scrape.js'

/**
 * Naming a site GoingElectric is the only source for.
 *
 * The one rule this file exists to enforce: **the scraped `name` is never used.**
 * It is contributor-written free text, often German, and frequently describes the
 * venue rather than the charger - `Ladesäule`, `Nissan Händler`,
 * `Wissenschaftszentrum Kopernikus`, `Best Western Premium`. Putting those on a
 * Polish map beside registry rows would misrepresent both what they are and where
 * they came from. Everything below builds a name out of identifiers and address
 * fields instead, all of which the operator issued or the postal system defines.
 *
 * Three sources, best first. Each is tried only when the one above it produced
 * nothing, and on the August 2026 scrape they split 519 / 470 / 456 across the
 * 1445 sites EIPA does not list:
 *
 *  1. **The operator registry.** An eMI3 id carries the party that issued it, and
 *     `PL-BB4` is a code the registry already maps - so `PL*BB4*E7278*9749*0*1`
 *     is a Tauron charger and can say so. This is preferred over the id itself
 *     because `Tauron, Kozielska 33, Gliwice` is a name a person can act on and
 *     `PL*BB4*E7278*97` is not. It is not a GoingElectric name: it is this repo's
 *     own brand registry, reached through an identifier.
 *  2. **The common EVSE prefix**, for a party the registry does not track. See
 *     {@link geEvsePrefixName}.
 *  3. **The address**, for a site with no usable id at all - which is 456 of them,
 *     including every Tesla site, because Tesla publishes no eMI3 ids.
 *
 * The street is part of the first form on purpose. Without it the 109 Tauron
 * sites fold onto 20-odd city names - `Tauron, Gliwice` 25 times over - and a
 * name that 25 pins share is not a name. With it, 1423 of 1445 are distinct; the
 * 22 that still collide are motorway service areas whose address genuinely is a
 * road number, which `siteAddressKeys` already refuses to key on for the same
 * reason.
 */

/**
 * The `PL-BB4` half of an eMI3 id - country and issuing party, no socket.
 *
 * `resolveOperator` matches EIPA's `code` field, and EIPA writes exactly this
 * pair, so the two sides meet without a second registry to maintain.
 */
export const geEvseOperatorCode = (evseId: string): string | undefined => {
  const [country, party] = evseId.trim().toUpperCase().split(/[*-]/)
  return country === undefined || country === '' || party === undefined || party === ''
    ? undefined
    : `${country}-${party}`
}

/**
 * The tracked brand every id at a site belongs to, or `undefined`.
 *
 * Unanimity is required. A site whose connectors were issued by two different
 * parties is two operators sharing a car park, and naming the whole pin after
 * whichever id sorted first would be a guess presented as a fact.
 */
export const geOperatorSlug = (evseIds: readonly string[]): OperatorSlug | undefined => {
  const codes = [...new Set(evseIds.map(geEvseOperatorCode).filter((code) => code !== undefined))]
  if (codes.length !== 1) return undefined

  const slug = resolveOperator(null, null, codes[0])
  return slug === OTHER_OPERATOR_SLUG ? undefined : slug
}

/**
 * The shortest EVSE id that every connector at a site starts with.
 *
 * `PL*GWN*00001` and `PL*GWN*00002` share `PL*GWN*0000`, which names the
 * physical installation rather than one of its sockets.
 *
 * Returns `undefined` when the common prefix has fewer than eight alphanumerics,
 * which is the case worth rejecting: a country code and a party id fit in eight,
 * so anything shorter has collapsed to naming the *network*. That happens for
 * real - a site holding two unrelated hardware groups (`PL*WUA*EQP*2978*1`
 * beside `PL*WUA*EPLALF*01*1`) shares only `PL*WUA*E` - and 50 sites all called
 * `PL*WUA*E` would be worse than no name at all.
 */
export const geEvsePrefixName = (evseIds: readonly string[]): string | undefined => {
  const ids = [...new Set(evseIds.map((id) => id.trim().toUpperCase()).filter((id) => id !== ''))]
  if (ids.length === 0) return undefined

  // The common prefix of a set is the common prefix of its lexicographic
  // extremes, so the whole set never has to be walked more than twice.
  const sorted = [...ids].sort()
  const first = sorted[0] ?? ''
  const last = sorted[sorted.length - 1] ?? ''

  let end = 0
  while (end < first.length && end < last.length && first[end] === last[end]) end += 1

  // A prefix ending on a separator names the field boundary, not a field.
  const prefix = first.slice(0, end).replace(/[*\-\s]+$/, '')
  return normalizeEvseId(prefix).length >= 8 ? prefix : undefined
}

const trimmed = (value: string | null | undefined): string | undefined => {
  const text = value?.trim() ?? ''
  return text === '' ? undefined : text
}

/** `Street 12, 00-000 City`, which is how EIPA names most of its own sites. */
const addressName = (address: GeAddress): string | undefined => {
  const street = trimmed(address.street)
  const city = trimmed(address.city)
  const locality = [trimmed(address.postcode), city].filter(Boolean).join(' ')
  return [street, locality].filter((part) => part !== '').join(', ') || undefined
}

const DISPLAY_NAMES = new Map(OPERATORS.map((brand) => [brand.slug, brand.displayName]))

export interface GeSiteNameInput {
  /** Every EVSE id at the site, in any order. */
  readonly evseIds: readonly string[]
  readonly address: GeAddress
}

/**
 * The published name for a GoingElectric-only site, and never its scraped one.
 *
 * Falls back to the site id last, which no real row reaches - every site in the
 * August 2026 scrape produced one of the three real forms - but a name is not
 * optional on {@link Site} and inventing an empty string would put a blank pin on
 * the map rather than an honest one.
 */
export const geSiteName = (input: GeSiteNameInput): string => {
  const slug = geOperatorSlug(input.evseIds)
  const brand = slug === undefined ? undefined : DISPLAY_NAMES.get(slug)
  if (brand !== undefined) {
    const parts = [brand, trimmed(input.address.street), trimmed(input.address.city)]
    return parts.filter((part) => part !== undefined).join(', ')
  }

  return geEvsePrefixName(input.evseIds) ?? addressName(input.address) ?? 'unknown'
}
